import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Plus, Trash2, Gauge, Pencil, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, km, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const EMPTY = {
  immatriculation: "",
  marque: "", modele: "", annee: new Date().getFullYear(), motorisation: "",
  puissance: "", carburant: "", boite_vitesses: "", kilometrage: 0,
  date_mise_en_circulation: "", fiche_technique: null,
};

const FT_KEYS = ["vin", "carrosserie", "puissance_fiscale", "cylindree", "nb_places",
  "genre", "finition", "code_moteur"];

// Ne garde que les valeurs simples renvoyées par l'API plaque (un objet ou un tableau
// injecté dans le formulaire ferait planter l'affichage).
const txt = (v) =>
  typeof v === "string" ? v : typeof v === "number" || typeof v === "boolean" ? String(v) : "";

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

const CARBURANTS = ["Essence", "Diesel", "Hybride", "Électrique", "GPL"];
const BOITES = ["Manuelle", "Automatique"];

export default function Vehicles() {
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [lookupMsg, setLookupMsg] = useState(null);

  const lookupPlate = async () => {
    if (!form.immatriculation.trim()) {
      setLookupMsg({ ok: false, text: "Saisissez d'abord une immatriculation." });
      return;
    }
    setLooking(true);
    setLookupMsg(null);
    try {
      const { data } = await api.post("/vehicles/lookup-plate", {
        immatriculation: form.immatriculation,
      });
      if (data.found) {
        trackEvent("plaque_validee");
        const v = data.vehicle || {};
        const ft = Object.fromEntries(
          FT_KEYS.map((k) => [k, txt(v[k])]).filter(([, val]) => val !== "")
        );
        setForm((f) => ({
          ...f,
          immatriculation: txt(v.immatriculation) || f.immatriculation,
          marque: txt(v.marque) || f.marque,
          modele: txt(v.modele) || f.modele,
          annee: num(v.annee) || f.annee,
          motorisation: txt(v.motorisation) || f.motorisation,
          puissance: txt(v.puissance) || f.puissance,
          carburant: txt(v.carburant) || f.carburant,
          boite_vitesses: txt(v.boite_vitesses) || f.boite_vitesses,
          date_mise_en_circulation: txt(v.date_mise_en_circulation) || f.date_mise_en_circulation,
          fiche_technique: Object.keys(ft).length ? ft : f.fiche_technique,
        }));
        const manquants = ["marque", "modele", "carburant", "puissance"].filter((k) => !txt(v[k]));
        setLookupMsg({
          ok: true,
          text: manquants.length
            ? "Véhicule trouvé. Complétez les champs restants manuellement."
            : "Véhicule trouvé, vérifiez les informations puis enregistrez.",
        });
        toast.success("Fiche préremplie");
      } else {
        setLookupMsg({ ok: false, text: txt(data.message) || "Plaque introuvable. Complétez les informations manuellement." });
      }
    } catch (err) {
      setLookupMsg({
        ok: false,
        text: "Recherche impossible pour le moment. Saisie manuelle possible.",
      });
    } finally {
      setLooking(false);
    }
  };

  const load = () => api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const closeDialog = (v) => {
    setOpen(v);
    if (!v && params.get("new")) setParams({});
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      annee: Number(form.annee),
      kilometrage: Number(form.kilometrage) || 0,
      date_mise_en_circulation: form.date_mise_en_circulation || null,
    };
    try {
      if (editId) {
        await api.put(`/vehicles/${editId}`, payload);
        toast.success("Véhicule modifié");
      } else {
        await api.post("/vehicles", payload);
        trackEvent("vehicule_enregistre", {
          prerempli_par_plaque: Boolean(form.fiche_technique),
        });
        toast.success("Véhicule ajouté");
      }
      setForm(EMPTY);
      setEditId(null);
      closeDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (v) => {
    setEditId(v.id);
    setLookupMsg(null);
    setForm({
      immatriculation: v.immatriculation || "",
      marque: v.marque, modele: v.modele, annee: v.annee,
      motorisation: v.motorisation || "", puissance: v.puissance || "",
      carburant: v.carburant || "", boite_vitesses: v.boite_vitesses || "",
      kilometrage: v.kilometrage || 0,
      date_mise_en_circulation: v.date_mise_en_circulation || "",
      fiche_technique: v.fiche_technique || null,
    });
    setOpen(true);
  };

  const startCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setLookupMsg(null);
    setOpen(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Véhicule supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="vehicles-page" className="space-y-8">
      <div className="flex items-end justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Garage</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Mes véhicules
          </h1>
        </div>
        <Button data-testid="open-vehicle-form-btn" onClick={startCreate}
          className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Ajouter
        </Button>
      </div>

      {vehicles.length === 0 ? (
        <div data-testid="vehicles-empty" className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <p className="font-display text-lg font-semibold text-slate-800">Votre garage est vide</p>
          <p className="mt-1 text-sm text-slate-500">Ajoutez votre premier véhicule pour démarrer.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {vehicles.map((v, i) => (
            <div key={v.id} data-testid={`vehicle-card-${v.id}`}
              className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm animate-fade-up"
              style={{ animationDelay: `${i * 70}ms` }}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-xl font-semibold text-slate-900">
                  {v.marque} {v.modele}
                </h3>
                <StatusBadge status={v.statut} />
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                {[
                  ["Immatriculation", v.immatriculation || "—"],
                  ["Année", v.annee],
                  ["Motorisation", v.motorisation || "—"],
                  ["Puissance", v.puissance || "—"],
                  ["Carburant", v.carburant || "—"],
                  ["Boîte", v.boite_vitesses || "—"],
                  ["1ère circulation", v.date_mise_en_circulation || "—"],
                ].map(([k, val]) => (
                  <div key={k}>
                    <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">{k}</dt>
                    <dd className="text-slate-700">{val}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="inline-flex items-center gap-2 font-display font-semibold text-slate-800">
                  <Gauge className="h-4 w-4 text-slate-400" /> {km(v.kilometrage)}
                </span>
                <div className="flex items-center gap-3">
                  <Link to={`/app/vehicules/${v.id}`} data-testid={`vehicle-detail-${v.id}`}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                    État
                  </Link>
                  <Link to={`/app/entretien?vehicle=${v.id}`} data-testid={`vehicle-logbook-${v.id}`}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                    Carnet
                  </Link>
                  <button onClick={() => startEdit(v)} data-testid={`vehicle-edit-${v.id}`}
                    className="text-slate-400 hover:text-blue-600 transition-colors">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(v.id)} data-testid={`vehicle-delete-${v.id}`}
                    className="text-slate-400 hover:text-red-600 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vehicle-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editId ? "Modifier le véhicule" : "Ajouter un véhicule"}
            </DialogTitle>
            <DialogDescription>
              Renseignez l'immatriculation pour préremplir la fiche, ou saisissez les informations
              manuellement.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="vehicle-form">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="space-y-2">
                <Label>Immatriculation</Label>
                <div className="flex items-center gap-2">
                  <Input value={form.immatriculation}
                    onChange={(e) => setForm({ ...form, immatriculation: e.target.value.toUpperCase() })}
                    data-testid="vehicle-immatriculation-input" placeholder="AB-123-CD" />
                  <Button type="button" onClick={lookupPlate} disabled={looking}
                    data-testid="plate-lookup-btn" className="rounded-full bg-slate-900 hover:bg-slate-800 shrink-0 disabled:opacity-60">
                    {looking ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4 mr-1.5" />
                    )}
                    {looking ? <span>Recherche…</span> : <span>Rechercher</span>}
                  </Button>
                </div>
              </div>
              {looking && (
                <p data-testid="plate-lookup-loading"
                  className="flex items-center gap-2 text-sm font-medium text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  Recherche du véhicule en cours…
                </p>
              )}
              {lookupMsg && (
                <p data-testid="plate-lookup-message"
                  className={`text-sm font-medium ${lookupMsg.ok ? "text-emerald-700" : "text-amber-700"}`}>
                  {lookupMsg.text}
                </p>
              )}
              <p className="text-xs text-slate-500">
                Renseignez la plaque puis « Rechercher » pour préremplir automatiquement la fiche.
                Tous les champs restent modifiables.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input required value={form.marque} onChange={set("marque")} data-testid="vehicle-marque-input" placeholder="Renault" />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input required value={form.modele} onChange={set("modele")} data-testid="vehicle-modele-input" placeholder="Clio" />
              </div>
              <div className="space-y-2">
                <Label>Année</Label>
                <Input required type="number" value={form.annee} onChange={set("annee")} data-testid="vehicle-annee-input" />
              </div>
              <div className="space-y-2">
                <Label>Motorisation</Label>
                <Input value={form.motorisation} onChange={set("motorisation")} data-testid="vehicle-motorisation-input" placeholder="1.5 dCi" />
              </div>
              <div className="space-y-2">
                <Label>Puissance</Label>
                <Input value={form.puissance} onChange={set("puissance")} data-testid="vehicle-puissance-input" placeholder="115 ch" />
              </div>
              <div className="space-y-2">
                <Label>Carburant</Label>
                <select value={form.carburant} onChange={set("carburant")} data-testid="vehicle-carburant-select"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                  <option value="">Choisir…</option>
                  {CARBURANTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Boîte de vitesses</Label>
                <select value={form.boite_vitesses} onChange={set("boite_vitesses")} data-testid="vehicle-boite-select"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                  <option value="">Choisir…</option>
                  {BOITES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Kilométrage actuel</Label>
                <Input type="number" value={form.kilometrage} onChange={set("kilometrage")} data-testid="vehicle-km-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date de première mise en circulation</Label>
              <Input type="date" value={form.date_mise_en_circulation} onChange={set("date_mise_en_circulation")} data-testid="vehicle-date-input" />
            </div>
            <Button type="submit" disabled={saving} data-testid="vehicle-submit-btn"
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? "Enregistrement…" : editId ? "Enregistrer les modifications" : "Enregistrer le véhicule"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
